import Title from '@cohort/components-xps/components/Title';
import {defaultErrorMessage} from '@cohort/shared/models';
import type {PrestashopCheckAccountCreationInput} from '@cohort/shared/schema/apps/prestashop';
import type {
  PrestashopGroupConfig,
  PrestashopGroupState,
  PrestashopGroupUsageData,
  PrestashopGroupUsageInput,
} from '@cohort/shared/schema/apps/prestashop/perks/group';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import type {PerkUsageComponentProps} from '@cohort/wallet/apps/PerkIntegration';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import useNotify from '@cohort/wallet/hooks/notify';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {AppsApi, OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {trackError} from '@cohort/wallet/lib/Tracking';
import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {shallow} from 'zustand/shallow';

export const PrestashopGroupPerkUsageComponent: React.FC<PerkUsageComponentProps> = ({
  access,
  ownership,
  onClose,
}) => {
  const doUsePerkAccess = useApi(OwnershipsApi.doUsePerkAccess);
  const watchPrestashopAccountCreation = useApi(AppsApi.executeAction);
  const {t} = useTranslation('app-prestashop', {
    keyPrefix: 'perkIntegrations.group.perkUsageComponent',
  });
  const {user, firebaseUser} = useUserStore(
    store => ({
      user: store.user,
      firebaseUser: store.firebaseUser,
    }),
    shallow
  );
  const notify = useNotify();

  const [perkUsageData, setPerkUsageData] = useState<PrestashopGroupUsageData>();

  const perkConfig = useMemo(
    () => access.perk.integration?.config as PrestashopGroupConfig,
    [access.perk.integration]
  );
  const perkState = useMemo(
    () => access.perk.integration?.state as PrestashopGroupState,
    [access.perk.integration]
  );

  const addQueryParam = useCallback((url: string, key: string, value: string) => {
    const urlObject = new URL(url);
    urlObject.searchParams.append(key, value);
    return urlObject.toString();
  }, []);

  const onGoToShopClick = useCallback(() => {
    window.open(
      addQueryParam(perkConfig.loginUrl, 'email', perkUsageData?.prestashopCustomerEmail ?? ''),
      '_blank',
      'noopener,noreferrer'
    );
  }, [addQueryParam, perkConfig.loginUrl, perkUsageData?.prestashopCustomerEmail]);

  const onCreateAccountClick = useCallback(() => {
    window.open(
      addQueryParam(perkConfig.registrationUrl, 'email', firebaseUser?.email ?? ''),
      '_blank',
      'noopener,noreferrer'
    );
    watchPrestashopAccountCreation({
      actionId: 'check-account-creation',
      appId: 'prestashop',
      userConnectionId: null,
      merchantConnectionId: access.perk.integration?.connectionId ?? '',
      input: {
        email: firebaseUser?.email ?? '',
        groupId: perkState.groupId,
      } as PrestashopCheckAccountCreationInput,
    })
      .then(() => {
        // Close the modal so the user re-opens it and sees the "Go to shop" button
        onClose();
      })
      .catch(trackError);
  }, [
    addQueryParam,
    perkConfig.registrationUrl,
    firebaseUser?.email,
    watchPrestashopAccountCreation,
    access.perk.integration?.connectionId,
    perkState.groupId,
    onClose,
  ]);

  useEffect(() => {
    doUsePerkAccess(ownership.id, access.id, null, {
      email: user?.email ?? '',
    } satisfies PrestashopGroupUsageInput)
      .then(res => {
        if (res.integration?.perkIntegrationId !== 'prestashop.group') {
          throw new Error('This is not a Prestashop perk');
        }
        const usageData = res.integration.data as PrestashopGroupUsageData;
        setPerkUsageData(usageData);
      })
      .catch(err => {
        if (isCohortError(err, 'perk.usages-limit-reached')) {
          notify('error', t('maxUsagesReached'), {autoClose: false});
          return;
        }
        notify('error', defaultErrorMessage);
      });
  }, [doUsePerkAccess, access, ownership, notify, t, user?.email]);

  if (perkUsageData === undefined) {
    return <Fragment />;
  }

  const userExists = perkUsageData.prestashopCustomerId !== null;
  const userEmail =
    perkUsageData.prestashopCustomerEmail === null
      ? firebaseUser?.email
      : perkUsageData.prestashopCustomerEmail;

  return (
    <Fragment>
      <div className="gap-y-2 pb-7">
        <Title
          content={t('title')}
          testId="use-prestashop-perk-nft-picker-title"
          classes="text-center md:text-left"
        />
        <Subtitle
          classes="text-center md:text-left"
          testId="use-prestashop-perk-nft-picker-subtitle"
          content={
            userExists
              ? t('subtitleCustomerAlreadyCreated', {email: userEmail})
              : t('subtitleCustomerDoesNotExist', {
                  email: userEmail,
                })
          }
        />
      </div>

      <div className="mt-6 flex flex-col gap-x-4 md:flex-row">
        {userExists ? (
          <PrimaryButton
            onClick={onGoToShopClick}
            text={t('buttonGoToShop')}
            className="outline-0 focus:ring-0 focus:ring-offset-0"
            tracking={{
              namespace: 'perks.usage',
              metadata: {
                appId: access.perk.integration?.perkIntegrationId,
                action: 'visitShop',
                ownershipId: ownership.id,
                accessId: access.id,
                perkId: access.perk.id,
              },
            }}
          />
        ) : (
          <PrimaryButton
            onClick={onCreateAccountClick}
            text={t('buttonCreateAccount')}
            className="outline-0 focus:ring-0 focus:ring-offset-0"
            tracking={{
              namespace: 'perks.usage',
              metadata: {
                appId: access.perk.integration?.perkIntegrationId,
                action: 'createAccount',
                ownershipId: ownership.id,
                accessId: access.id,
                perkId: access.perk.id,
              },
            }}
          />
        )}

        <PrimaryButton
          onClick={() => {
            onClose();
          }}
          text={t('close')}
          className="mt-3 outline-0 focus:ring-0 focus:ring-offset-0 md:mt-0"
          tracking={{
            namespace: 'perks.usage',
            metadata: {
              appId: access.perk.integration?.perkIntegrationId,
              action: 'close',
              ownershipId: ownership.id,
              accessId: access.id,
              perkId: access.perk.id,
            },
          }}
        />
      </div>
    </Fragment>
  );
};
